import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { DEFAULT_SESSION_KIND, type SessionKind } from '@/constants/session-kind'
import { LANGUAGES, sanitizeLanguageCode, type Language } from '@/constants/languages'
import { sanitizeUiLocale, type UiLocale } from '@/constants/ui-locale'
import { type SessionUser } from '@/constants/auth'
import { COPILOT_PANEL_DEFAULT_WIDTH } from '@/pages/Meetings/components/Copilot/copilotPanelWidth'
import type { AssistModel } from '@/pages/Meetings/components/Copilot/assistModel'
import type { AssistRoute } from '@/pages/Meetings/components/Copilot/assist.types'
import type { TranscriptionContext } from '@soniox/client'
import type { MeetingInviteEvent } from '@ai-meeting/shared'

interface Message {
  id: string
  timestamp: string
  text?: string
  translation?: string
  speaker?: string
  [key: string]: unknown
}

interface MeetingContext extends TranscriptionContext {}

type MeetingMode = 'one_way' | 'two_way'

interface MeetingSetup {
  /** Translation direction. Not the Mode the create form labels as such — see `sessionKind`. */
  mode: MeetingMode
  /** What the session is for; picks the live screen's starting panel layout (BRAI-416). */
  sessionKind: SessionKind
  microphoneDeviceId: string
  microphoneLabel: string
  shareTabAudio: boolean
  /**
   * Whether a screenshot of the shared tab goes to the AI every few seconds.
   *
   * Opt-in: it used to happen on every share-tab meeting with nothing on screen
   * saying so. Only ever possible alongside `shareTabAudio`, which is where the
   * borrowed display stream comes from.
   */
  sendScreenFrames: boolean
  /** False when no microphone hardware was detected in the create-meeting modal. */
  hasMicrophone: boolean
  glossaryId: string | null
  transcriptionSource: 'browser' | 'teams-native' | 'google-meet-native'
  nativeMeetingReference: string
}

type SttProvider = 'soniox' | 'azure' | 'google_live_translate' | 'gpt-realtime-whisper'

interface Meeting {
  id: string
  title: string
  createdAt: string
  updatedAt: string
  sourceLang: string
  targetLang: string
  sttProvider: SttProvider
  /** How this session was created; drives /live vs /record routing (BRAI-460). */
  sessionKind?: SessionKind
  messages: Message[]
  context: MeetingContext
  /**
   * Grouping key declared to the AI proxy instead of this meeting's own id, so
   * several machines land in one room (BRAI-533). Null for a meeting nobody
   * shared, in which case the room is a room of one.
   */
  sharedRoomId?: string | null
  /**
   * Backlog project this meeting belongs to, as the create form declared it.
   *
   * Held here for the same reason `sharedRoomId` is: it is decided once when
   * the meeting is created and then needed on the recording socket, which has
   * no other way back to the server row it was written to.
   */
  backlogProjectKey?: string | null
}

interface PendingSavedMeeting {
  clientId: string
  title: string
  sourceLang: string
  targetLang: string
  durationSeconds: number
  startedAt: number
}

interface SaveToast {
  id: number
  kind: 'success' | 'error'
  message: string
}

interface Settings {
  sourceLang: string
  targetLang: string
  audioSource: 'mic' | 'system'
  context: MeetingContext
  meetingSetup: MeetingSetup
  pendingStartMeetingId: string | null
  /**
   * Let the server offer to merge this machine into a shared room when it
   * hears another one on the same call.
   *
   * Optional, and read as `!== false`, because `persist` replaces the whole
   * stored `settings` object rather than merging key by key: every reader who
   * has run the app before has a stored object without this field, so a
   * required boolean would be a lie at runtime and would read as "off" for
   * everyone who upgraded.
   *
   * Per browser, not per account — an accepted limit, since there is no user
   * settings table.
   */
  autoJoinSharedRoom?: boolean
  /**
   * Which assistant answers Copilot, remembered for the meetings after this
   * one.
   *
   * Optional for the reason above, and parsed rather than trusted: absent — or
   * a name an older build once wrote — means "never chosen", which the panel
   * shows as the server's own default.
   */
  assistModel?: AssistModel
  /**
   * Which kinds of question the Copilot answers, remembered for the meetings
   * after this one.
   *
   * Optional and parsed, for the reason the field above is: `persist` replaces
   * the whole stored `settings` object, so an older reader has one without it.
   * Absent means "never chosen", which reads as both routes — the same answer
   * the server gives an absent `assistRoutes`.
   */
  assistRoutes?: AssistRoute[]
}

interface StoreState {
  settings: Settings
  meetings: Meeting[]
  currentMeetingId: string | null
  showCreateMeetingModal: boolean
  sidebarOpen: boolean
  theme: 'light' | 'dark'
  uiLocale: UiLocale
  isAuthenticated: boolean
  user: SessionUser | null
  /**
   * Meetings the user owns, the trash included, from the API (set by Home
   * and History). Only a fallback seed for `Meeting#N` when the modal cannot
   * reach the list, which is why it counts deleted meetings: they still hold
   * their titles. See `resolveDefaultMeetingName`.
   */
  savedMeetingCount: number
  /**
   * Meetings whose background save is still running, as a row on Home.
   *
   * Deliberately NOT persisted. A row here is the progress of work happening
   * in this tab, and only that tab's own code clears it — the clearing runs
   * after the screen has navigated away, inside a promise nothing awaits. A
   * reload, a closed tab or a throw in that promise therefore left the row
   * behind for good, and restoring it from storage meant Home showed
   * meetings saving that nothing was saving: one more every time, which is
   * what BRAI-730 reports.
   *
   * Nothing is lost by dropping them. The meeting itself was written to the
   * server before this row was added; the row only tracks the audio upload
   * and the minutes request that follow it.
   */
  pendingSavedMeetings: PendingSavedMeeting[]
  /** BRAI-460 job ids keyed by meeting id — polled after End Meeting (flow C). */
  internalMinutesJobByMeetingId: Record<string, string>
  /** Set when internal End Meeting failed to queue POST /minutes/audio (not persisted). */
  internalMinutesAudioQueueFailedIds: Record<string, true>
  /**
   * True once this tab has been identified as the Microsoft Teams side panel
   * (BRAI-572), which decides the live screen's opening panel layout.
   *
   * Deliberately absent from `partialize`: it describes the frame the app was
   * loaded into, not a preference. Persisted, it would survive into an ordinary
   * browser session on the same machine and lay out a screen that is not in
   * Teams at all. The panel sets it again on every load, which is cheap, so
   * there is nothing to gain by keeping it.
   */
  isTeamsPanel: boolean
  /**
   * The reader skipped the one-time Backlog feature notice, or already had a
   * working connection when it was offered.
   *
   * A new field on purpose: the previous banner stored its own dismissal, and
   * reusing that flag hid this notice from people who had never seen it.
   */
  backlogFeatureNoticeDismissed: boolean
  dismissBacklogNudge: () => void
  /**
   * Width the reader dragged the Copilot panel to, in pixels (BRAI-681).
   *
   * Persisted, because it is a layout preference rather than a thing about
   * this meeting — the same reason `sidebarOpen` beside it is. Stored raw and
   * clamped at the point of use: the window it has to fit inside is not the
   * one it was dragged in, and a value saved on a wide screen would otherwise
   * crowd out the transcript on a narrow one.
   */
  copilotPanelWidth: number
  setCopilotPanelWidth: (width: number) => void
  saveToast: SaveToast | null
  /**
   * Set when a meeting was created but sharing it failed (BRAI-533). Lives in
   * the store, not in the create modal, because the modal unmounts on the very
   * navigation that follows a successful create — a message held there would
   * never be seen. Null once dismissed.
   */
  shareOnCreateError: string | null
  /**
   * Invite accepted by the user (BRAI-533), read once by the create modal the
   * Join button opens. It carries both the shared room and its immutable
   * language pair so every member declares an identical proxy session.
   */
  pendingMeetingInvite: MeetingInviteEvent | null

  setSession: (user: SessionUser, accessToken: string, refreshToken?: string) => void
  patchSessionUser: (patch: Partial<SessionUser>) => void
  setSavedMeetingCount: (count: number) => void
  addPendingSavedMeeting: (meeting: PendingSavedMeeting) => void
  removePendingSavedMeeting: (clientId: string) => void
  setInternalMinutesJobId: (meetingId: string, jobId: string) => void
  clearInternalMinutesJobId: (meetingId: string) => void
  markInternalMinutesAudioQueueFailed: (meetingId: string) => void
  clearInternalMinutesAudioQueueFailed: (meetingId: string) => void
  /** One-way: a tab that is the Teams panel never stops being one. */
  markTeamsPanel: () => void
  showSaveToast: (toast: Omit<SaveToast, 'id'>) => void
  clearSaveToast: (id?: number) => void
  setShareOnCreateError: (message: string | null) => void
  /** Opens the create modal on an invite, carrying the room to join. */
  acceptMeetingInvite: (invite: MeetingInviteEvent) => void
  logout: () => void
  toggleTheme: () => void
  getCurrentMeeting: () => Meeting | null
  /**
   * Seed the store with a meeting row already persisted on the backend. The
   * caller passes the backend-assigned UUID as the id so the live UI and the
   * finished detail page share the same identifier.
   */
  createMeeting: (payload: {
    id: string
    title: string
    sourceLang: string
    targetLang: string
    sttProvider: SttProvider
    sessionKind?: SessionKind
    context?: MeetingContext
    /** Room this meeting joins, when it was created from an invite (BRAI-533). */
    sharedRoomId?: string | null
    /** Backlog project the create form declared; null when it named none. */
    backlogProjectKey?: string | null
  }) => Meeting
  selectMeeting: (id: string) => void
  deleteMeeting: (id: string) => void
  updateMeeting: (id: string, updates: Partial<Meeting>) => void
  addMessage: (meetingId: string, message: Omit<Message, 'id' | 'timestamp'>) => Message
  updateLastMessage: (meetingId: string, updates: Partial<Message>) => void
  clearMeeting: (id: string) => void
  setSourceLang: (lang: string) => void
  setTargetLang: (lang: string) => void
  setSettings: (newSettings: Partial<Settings>) => void
  clearPendingStartMeeting: () => void
  openCreateMeetingModal: () => void
  closeCreateMeetingModal: () => void
  toggleSidebar: () => void
  setSidebarOpen: (open: boolean) => void
  setUiLocale: (locale: UiLocale) => void
}

const DEFAULT_MEETING_SETUP: MeetingSetup = {
  mode: 'one_way',
  sessionKind: DEFAULT_SESSION_KIND,
  microphoneDeviceId: '',
  microphoneLabel: 'MacBook Air Microphone (Built-in)',
  shareTabAudio: true,
  sendScreenFrames: false,
  hasMicrophone: true,
  glossaryId: null,
  transcriptionSource: 'browser',
  nativeMeetingReference: '',
}

function buildMeeting(payload: {
  id: string
  title: string
  sourceLang: string
  targetLang: string
  sttProvider: SttProvider
  sessionKind?: SessionKind
  context?: MeetingContext
  sharedRoomId?: string | null
  backlogProjectKey?: string | null
}): Meeting {
  const now = new Date().toISOString()
  return {
    id: payload.id,
    title: payload.title,
    createdAt: now,
    updatedAt: now,
    sourceLang: payload.sourceLang,
    targetLang: payload.targetLang,
    sttProvider: payload.sttProvider,
    ...(payload.sessionKind ? { sessionKind: payload.sessionKind } : {}),
    messages: [],
    context: payload.context ?? {},
    sharedRoomId: payload.sharedRoomId ?? null,
    backlogProjectKey: payload.backlogProjectKey ?? null,
  }
}

export const useStore = create<StoreState>()(
  persist(
    (set, get) => ({
      settings: {
        sourceLang: '',
        targetLang: 'vi',
        openaiApiKey: '',
        audioSource: 'mic',
        context: {},
        meetingSetup: DEFAULT_MEETING_SETUP,
        pendingStartMeetingId: null,
        autoJoinSharedRoom: true,
      },

      meetings: [],
      currentMeetingId: null,
          showCreateMeetingModal: false,
      sidebarOpen: true,
      theme: 'light',
      uiLocale: 'en',
      isAuthenticated: false,
      user: null,
      savedMeetingCount: 0,
      pendingSavedMeetings: [],
      internalMinutesJobByMeetingId: {},
      internalMinutesAudioQueueFailedIds: {},
      isTeamsPanel: false,
      backlogFeatureNoticeDismissed: false,
      copilotPanelWidth: COPILOT_PANEL_DEFAULT_WIDTH,
      saveToast: null,
      shareOnCreateError: null,
      pendingMeetingInvite: null,

      setSession: (user, accessToken, refreshToken) => {
        try {
          localStorage.setItem('accessToken', accessToken)
          // Only written when one was issued: the OAuth redirect flow gets its
          // refresh token a round trip later, and must not clear the one it
          // already has in the meantime.
          if (refreshToken) localStorage.setItem('refreshToken', refreshToken)
        } catch {
          /* ignore quota / private mode */
        }
        set({ isAuthenticated: true, user })
      },

      patchSessionUser: (patch) =>
        set((state) =>
          state.user
            ? {
                user: {
                  ...state.user,
                  ...patch,
                },
              }
            : {},
        ),

      logout: () => {
        try {
          localStorage.removeItem('accessToken')
          localStorage.removeItem('refreshToken')
        } catch {
          /* ignore */
        }
        set({
          isAuthenticated: false,
          user: null,
          savedMeetingCount: 0,
          shareOnCreateError: null,
          pendingMeetingInvite: null,
        })
      },

      setSavedMeetingCount: (count) => set({ savedMeetingCount: count }),

      addPendingSavedMeeting: (meeting) =>
        set((state) => ({
          pendingSavedMeetings: [
            meeting,
            ...state.pendingSavedMeetings.filter((item) => item.clientId !== meeting.clientId),
          ],
        })),

      removePendingSavedMeeting: (clientId) =>
        set((state) => ({
          pendingSavedMeetings: state.pendingSavedMeetings.filter(
            (meeting) => meeting.clientId !== clientId
          ),
        })),

      setInternalMinutesJobId: (meetingId, jobId) =>
        set((state) => ({
          internalMinutesJobByMeetingId: {
            ...state.internalMinutesJobByMeetingId,
            [meetingId]: jobId,
          },
        })),

      clearInternalMinutesJobId: (meetingId) =>
        set((state) => {
          const { [meetingId]: _removed, ...rest } = state.internalMinutesJobByMeetingId
          return { internalMinutesJobByMeetingId: rest }
        }),

      markInternalMinutesAudioQueueFailed: (meetingId) =>
        set((state) => ({
          internalMinutesAudioQueueFailedIds: {
            ...state.internalMinutesAudioQueueFailedIds,
            [meetingId]: true,
          },
        })),

      clearInternalMinutesAudioQueueFailed: (meetingId) =>
        set((state) => {
          const { [meetingId]: _removed, ...rest } = state.internalMinutesAudioQueueFailedIds
          return { internalMinutesAudioQueueFailedIds: rest }
        }),

      markTeamsPanel: () => set({ isTeamsPanel: true }),

      setShareOnCreateError: (message) => set({ shareOnCreateError: message }),

      acceptMeetingInvite: (invite) =>
        set({ pendingMeetingInvite: invite, showCreateMeetingModal: true }),

      showSaveToast: (toast) =>
        set({
          saveToast: {
            id: Date.now(),
            ...toast,
          },
        }),

      clearSaveToast: (id) =>
        set((state) => ({
          saveToast: id === undefined || state.saveToast?.id === id ? null : state.saveToast,
        })),

      toggleTheme: () => set((state) => ({ theme: state.theme === 'dark' ? 'light' : 'dark' })),

      getCurrentMeeting: () => {
        const state = get()
        return state.meetings.find((m) => m.id === state.currentMeetingId) ?? null
      },

      createMeeting: (payload) => {
        const newMeeting = buildMeeting(payload)
        set((state) => ({
          meetings: [newMeeting, ...state.meetings.filter((m) => m.id !== newMeeting.id)],
          currentMeetingId: newMeeting.id,
        }))
        return newMeeting
      },

      selectMeeting: (id) => set({ currentMeetingId: id }),

      deleteMeeting: (id) =>
        set((state) => ({
          meetings: state.meetings.filter((m) => m.id !== id),
          currentMeetingId:
            state.currentMeetingId === id
              ? (state.meetings.find((m) => m.id !== id)?.id ?? null)
              : state.currentMeetingId,
        })),

      updateMeeting: (id, updates) =>
        set((state) => ({
          meetings: state.meetings.map((m) =>
            m.id === id ? { ...m, ...updates, updatedAt: new Date().toISOString() } : m
          ),
        })),

      addMessage: (meetingId, message) => {
        const messageWithId: Message = {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          timestamp: new Date().toISOString(),
          ...message,
        }
        set((state) => ({
          meetings: state.meetings.map((m) => {
            if (m.id !== meetingId) return m
            let title = m.title
            if (m.messages.length === 0 && message.text) {
              const t = String(message.text)
              title = t.slice(0, 50) + (t.length > 50 ? '...' : '')
            }
            return {
              ...m,
              title,
              messages: [...m.messages, messageWithId],
              updatedAt: new Date().toISOString(),
            }
          }),
        }))
        return messageWithId
      },

      updateLastMessage: (meetingId, updates) => {
        set((state) => ({
          meetings: state.meetings.map((m) => {
            if (m.id !== meetingId || m.messages.length === 0) return m
            const messages = [...m.messages]
            messages[messages.length - 1] = { ...messages[messages.length - 1], ...updates }
            return { ...m, messages, updatedAt: new Date().toISOString() }
          }),
        }))
      },

      clearMeeting: (id) => {
        set((state) => ({
          meetings: state.meetings.map((m) =>
            m.id === id
              ? { ...m, messages: [], title: 'New Meeting', updatedAt: new Date().toISOString() }
              : m
          ),
        }))
      },

      setSourceLang: (lang) =>
        set((state) => ({
          settings: { ...state.settings, sourceLang: lang },
          meetings: state.meetings.map((m) =>
            m.id === state.currentMeetingId ? { ...m, sourceLang: lang } : m
          ),
        })),

      setTargetLang: (lang) =>
        set((state) => ({
          settings: { ...state.settings, targetLang: lang },
          meetings: state.meetings.map((m) =>
            m.id === state.currentMeetingId ? { ...m, targetLang: lang } : m
          ),
        })),

      setSettings: (newSettings) =>
        set((state) => ({
          settings: {
            ...state.settings,
            ...newSettings,
            context: {
              ...state.settings.context,
              ...(newSettings.context ?? {}),
            },
            meetingSetup: {
              ...state.settings.meetingSetup,
              ...(newSettings.meetingSetup ?? {}),
            },
          },
        })),

      clearPendingStartMeeting: () =>
        set((state) => ({
          settings: { ...state.settings, pendingStartMeetingId: null },
        })),

      openCreateMeetingModal: () =>
        set({ showCreateMeetingModal: true, pendingMeetingInvite: null }),

      closeCreateMeetingModal: () =>
        // Clearing the room key here matters: left behind, the next ordinary
        // "New meeting" would silently join whatever room the last invite named.
        set({ showCreateMeetingModal: false, pendingMeetingInvite: null }),

      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),

      setSidebarOpen: (open) => set({ sidebarOpen: open }),

      setUiLocale: (locale) => set({ uiLocale: locale }),

      dismissBacklogNudge: () => set({ backlogFeatureNoticeDismissed: true }),

      setCopilotPanelWidth: (width) => set({ copilotPanelWidth: width }),
    }),
    {
      name: 'my-translator-storage',
      partialize: (state) => ({
        settings: state.settings,
        meetings: state.meetings,
        currentMeetingId: state.currentMeetingId,
        internalMinutesJobByMeetingId: state.internalMinutesJobByMeetingId,
        sidebarOpen: state.sidebarOpen,
        backlogFeatureNoticeDismissed: state.backlogFeatureNoticeDismissed,
        copilotPanelWidth: state.copilotPanelWidth,
        theme: state.theme,
        uiLocale: state.uiLocale,
        isAuthenticated: state.isAuthenticated,
        user: state.user,
      }),
      merge: (persistedState, currentState) => {
        if (!persistedState || typeof persistedState !== 'object') return currentState
        const p = persistedState as Partial<StoreState>
        const next = { ...currentState, ...p }
        if (p.settings) {
          next.settings = {
            ...currentState.settings,
            ...p.settings,
            context: {
              ...currentState.settings.context,
              ...(p.settings.context && typeof p.settings.context === 'object'
                ? p.settings.context
                : {}),
              text:
                typeof p.settings.context?.text === 'string'
                  ? p.settings.context.text
                  : (currentState.settings.context?.text ?? ''),
            },
            meetingSetup: {
              ...DEFAULT_MEETING_SETUP,
              ...(p.settings.meetingSetup && typeof p.settings.meetingSetup === 'object'
                ? p.settings.meetingSetup
                : {}),
            },
            pendingStartMeetingId:
              typeof p.settings.pendingStartMeetingId === 'string'
                ? p.settings.pendingStartMeetingId
                : null,
            sourceLang: sanitizeLanguageCode(
              p.settings.sourceLang,
              currentState.settings.sourceLang
            ),
            targetLang: sanitizeLanguageCode(
              p.settings.targetLang,
              currentState.settings.targetLang
            ),
          }
        }
        if (typeof p.sidebarOpen === 'boolean') {
          next.sidebarOpen = p.sidebarOpen
        }
        next.uiLocale = sanitizeUiLocale(p.uiLocale, currentState.uiLocale)
        if (Array.isArray(p.meetings)) {
          // Only accept meetings persisted with string ids (backend UUIDs). Any
          // legacy row created with a numeric client id is discarded on merge
          // so live/detail routes never receive a stale number id.
          next.meetings = p.meetings
            .filter((m) => typeof m?.id === 'string' && m.id.length > 0)
            .map((m) => ({
              ...m,
              sourceLang: sanitizeLanguageCode(m.sourceLang, ''),
              targetLang: sanitizeLanguageCode(m.targetLang, 'vi'),
            }))
        }
        if (typeof p.currentMeetingId !== 'string') {
          next.currentMeetingId = null
        }
        if (p.internalMinutesJobByMeetingId && typeof p.internalMinutesJobByMeetingId === 'object') {
          next.internalMinutesJobByMeetingId = Object.fromEntries(
            Object.entries(p.internalMinutesJobByMeetingId).filter(
              ([meetingId, jobId]) => meetingId.length > 0 && typeof jobId === 'string' && jobId.length > 0
            )
          )
        }
        if (p.user && typeof p.user === 'object' && p.user.email) {
          next.user = {
            ...p.user,
            displayName:
              typeof p.user.displayName === 'string' && p.user.displayName.trim()
                ? p.user.displayName
                : p.user.email,
          }
        }
        return next
      },
    }
  )
)

export { LANGUAGES }
export type {
  Language,
  Meeting,
  MeetingContext,
  MeetingMode,
  MeetingSetup,
  Message,
  PendingSavedMeeting,
  SaveToast,
  Settings,
}
export type { UiLocale } from '@/constants/ui-locale'
